/**
 * First-run onboarding versions. Keep in sync with
 * `api/lib/onboarding-versions.ts`. Bump TERMS_VERSION when the legal copy
 * changes enough to require re-acceptance; bump TOUR_VERSION when the
 * module tour steps change.
 */
export const TERMS_VERSION = '2026-08-03';
export const TOUR_VERSION = '2026-07-25';

const POST_ONBOARDING_REDIRECT_KEY = 'suturia:post-onboarding-redirect';

/** Remember a deep link to restore after first-run onboarding finishes. */
export function rememberPostOnboardingRedirect(path: string): void {
  if (
    !path ||
    path === '/' ||
    path.startsWith('/login') ||
    path.startsWith('/onboarding')
  ) {
    return;
  }
  try {
    sessionStorage.setItem(POST_ONBOARDING_REDIRECT_KEY, path);
  } catch {
    // sessionStorage unavailable
  }
}

/** Read and clear the saved post-onboarding destination. */
export function consumePostOnboardingRedirect(fallback = '/'): string {
  try {
    const path = sessionStorage.getItem(POST_ONBOARDING_REDIRECT_KEY);
    sessionStorage.removeItem(POST_ONBOARDING_REDIRECT_KEY);
    if (
      path &&
      path.startsWith('/') &&
      !path.startsWith('/login') &&
      !path.startsWith('/onboarding')
    ) {
      return path;
    }
  } catch {
    // sessionStorage unavailable
  }
  return fallback;
}

export type OnboardingStep = 'terms' | 'profile' | 'tour' | 'done';

export interface OnboardingStatus {
  termsAccepted: boolean;
  profileComplete: boolean;
  tourSeen: boolean;
  next: OnboardingStep;
}

/** Pure: decide the next onboarding gate from persisted flags. */
export function resolveOnboardingStep(input: {
  termsVersion: string | null | undefined;
  onboardingCompleted: boolean;
  tourSeenVersion: string | null | undefined;
  requiredTermsVersion?: string;
  requiredTourVersion?: string;
}): OnboardingStatus {
  const requiredTerms = input.requiredTermsVersion ?? TERMS_VERSION;
  const requiredTour = input.requiredTourVersion ?? TOUR_VERSION;
  const termsAccepted = input.termsVersion === requiredTerms;
  const profileComplete = input.onboardingCompleted;
  const tourSeen = input.tourSeenVersion === requiredTour;
  let next: OnboardingStep = 'done';
  if (!termsAccepted) next = 'terms';
  else if (!profileComplete) next = 'profile';
  else if (!tourSeen) next = 'tour';
  return { termsAccepted, profileComplete, tourSeen, next };
}

/**
 * Prefer the version that matches the current tour, then server, then local.
 * Used so a device that already finished the tour still skips it while the
 * server flag catches up after logout wipe of prefs.
 */
export function effectiveTourSeenVersion(
  serverVersion: string | null | undefined,
  localVersion: string | null | undefined,
  requiredTourVersion: string = TOUR_VERSION,
): string | null {
  if (serverVersion === requiredTourVersion || localVersion === requiredTourVersion) {
    return requiredTourVersion;
  }
  return serverVersion ?? localVersion ?? null;
}
